/**
 * Structural (deep) equality for the plain data this app moves around:
 * primitives, arrays, plain objects, `Date`s and Firestore value classes that
 * expose `isEqual` (Timestamp / GeoPoint).
 *
 * Purpose: KEEP a previous object's identity when a live snapshot or a poll
 * re-delivers unchanged data. Zustand selectors, `useMemo` chains and
 * `React.memo` all compare by reference, so a fresh-but-equal object is a
 * "something changed" signal that cascades into expensive recomputes (the
 * 30s job heartbeat re-running the whole Path-to-GO build, 2026-10-07).
 *
 * The semantics are deliberately strict so the helper is purely an identity
 * optimisation and never changes WHAT a merge or setter produces:
 *   - own enumerable keys must match exactly (`{ a: undefined }` ≠ `{}`);
 *   - arrays compare element-wise by index;
 *   - objects with different prototypes are never equal;
 *   - `Map` / `Set` / functions / symbols compare by reference only.
 * Inputs are expected to be acyclic (Firestore / JSON data); a cyclic graph
 * would recurse without bound.
 */
export function structuralEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!structuralEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;
  if (a instanceof Date) return a.getTime() === (b as Date).getTime();
  // Collections carry no stable structural identity we want to walk here.
  if (a instanceof Map || a instanceof Set) return false;
  // Firestore `Timestamp` / `GeoPoint` (and any value class with the same
  // contract) know how to compare themselves.
  const isEqual = (a as { isEqual?: unknown }).isEqual;
  if (typeof isEqual === 'function') {
    return Boolean((isEqual as (other: unknown) => unknown).call(a, b));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  for (const key of leftKeys) {
    if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
    if (!structuralEqual(left[key], right[key])) return false;
  }
  return true;
}

/**
 * True when two arrays hold the SAME elements by reference, in order. The
 * cheap check a store updater runs after a per-element identity-preserving
 * merge: if every element survived unchanged, the old array (and the state
 * holding it) can be returned as-is and no subscriber is notified.
 */
export function sameArrayElements<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}
